import type { PublicTesterProfile } from '@runhuman/shared';
import { DEVICE_META, LANGUAGE_LABELS, sortedDevices } from '@runhuman/shared';

interface AboutTabProps {
  readonly profile: PublicTesterProfile;
}

/**
 * Activity-side mirror of `<InternalTesterProfileDialog>` AboutTab — same
 * stats, facts and bio sections rendered with the Activity's CSS tokens.
 * Source of truth for the field set is `PublicTesterProfile`; both
 * surfaces consume the same shape so adding a bio field on the server
 * surfaces it here automatically.
 */
export function AboutTab({ profile }: AboutTabProps) {
  return (
    <div className="profile-modal__about">
      <Stats profile={profile} />
      <Facts profile={profile} />
      {hasBio(profile.bio) && <Bio bio={profile.bio} />}
    </div>
  );
}

function Stats({ profile }: { profile: PublicTesterProfile }) {
  // The internal-review Performance Rating (#4951) is intentionally not shown
  // on this community-facing surface — it stays on the internal/admin profiles.
  return (
    <div className="profile-modal__stats">
      <Stat
        label="Tests completed"
        value={String(profile.metrics.totalJobsCompleted)}
        icon="fa-check-circle"
      />
    </div>
  );
}

function Stat({ label, value, icon }: { label: string; value: string; icon: string }) {
  return (
    <div className="profile-modal__stat">
      <i className={`fa-solid ${icon} profile-modal__stat-icon`} aria-hidden="true" />
      <span className="profile-modal__stat-value">{value}</span>
      <span className="profile-modal__stat-label">{label}</span>
    </div>
  );
}

function Facts({ profile }: { profile: AboutTabProps['profile'] }) {
  const joinedLabel = new Date(profile.createdAt).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
  });
  const devicesLabel = sortedDevices(profile.devices)
    .map((d) => DEVICE_META[d].label)
    .join(', ');
  const languagesLabel = profile.languages.map((l) => LANGUAGE_LABELS[l]).join(', ');
  return (
    <div className="profile-modal__section">
      <Row label="Joined" value={joinedLabel} />
      {profile.devices.length > 0 && <Row label="Devices" value={devicesLabel} />}
      {profile.languages.length > 0 && <Row label="Languages" value={languagesLabel} />}
    </div>
  );
}

function Bio({ bio }: { bio: PublicTesterProfile['bio'] }) {
  return (
    <div className="profile-modal__section">
      <h3 className="profile-modal__section-title">Personal</h3>
      {bio.funFact && <Row label="Fun fact" value={bio.funFact} />}
      {bio.favoriteIceCream && <Row label="Favorite ice cream" value={bio.favoriteIceCream} />}
      {bio.hobbies.length > 0 && <Row label="Hobbies" value={bio.hobbies.join(', ')} />}
      {bio.favoriteMovies.length > 0 && (
        <Row label="Favorite movies" value={bio.favoriteMovies.join(', ')} />
      )}
      {bio.favoriteApps.length > 0 && (
        <Row label="Favorite apps" value={bio.favoriteApps.join(', ')} />
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="profile-modal__row">
      <span className="profile-modal__row-label">{label}</span>
      <span className="profile-modal__row-value">{value}</span>
    </div>
  );
}

function hasBio(bio: PublicTesterProfile['bio']): boolean {
  return Boolean(
    bio.funFact ||
      bio.favoriteIceCream ||
      bio.hobbies.length ||
      bio.favoriteMovies.length ||
      bio.favoriteApps.length,
  );
}
